import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3, FileText, Send, CheckCircle2, XCircle, DollarSign,
  TrendingUp, PieChart, Loader2, ArrowUpRight, ArrowDownRight,
  Layers, Mail, Target
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  hook: 'Executive Summary',
  pain_points: 'Client Challenges',
  value_proposition: 'Our Solution',
  scope_of_work: 'Scope of Work',
  deliverables: 'Deliverables',
  pricing: 'Investment',
  timeline: 'Timeline',
  social_proof: 'Why Choose Us',
  call_to_action: 'Next Steps',
};

const SECTION_COLORS = {
  hook: '#8B5CF6',
  pain_points: '#EF4444',
  value_proposition: '#10B981',
  scope_of_work: '#3B82F6',
  deliverables: '#F59E0B',
  pricing: '#8B5CF6',
  timeline: '#06B6D4',
  social_proof: '#EC4899',
  call_to_action: '#10B981',
};

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
}

function StatCard({ icon: Icon, label, value, subtext, color, bg }) {
  return (
    <Card className="border-gray-200" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
            {subtext && <p className="text-[10px] text-gray-400 mt-0.5">{subtext}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-600 w-32 truncate">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-8 text-right">{value}</span>
    </div>
  );
}

function FunnelBar({ label, count, pct, color, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
      <Icon className="h-4 w-4 flex-shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-800">{label}</span>
          <span className="text-xs font-bold text-gray-900">{count}</span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
      </div>
      <span className="text-[10px] text-gray-500 w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

export default function ProposalAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data: analytics } = await axios.get(`${API}/api/email/proposals/analytics`);
      setData(analytics);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Unable to load analytics</p>
      </div>
    );
  }

  const total = data.total_proposals;
  const funnelSteps = [
    { label: 'Draft', count: data.draft_count, color: '#6B7280', icon: FileText },
    { label: 'Sent', count: data.sent_count, color: '#3B82F6', icon: Send },
    { label: 'Accepted', count: data.accepted_count, color: '#10B981', icon: CheckCircle2 },
    { label: 'Rejected', count: data.rejected_count, color: '#EF4444', icon: XCircle },
  ];

  const maxSectionUsage = Math.max(...Object.values(data.section_usage || {}), 1);

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="proposal-analytics-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
          <BarChart3 className="h-5 w-5 text-violet-500" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Proposal Analytics<PageInfoTooltip pageKey="proposal-analytics" /></h1>
          <p className="text-xs text-gray-400">Track proposal performance, conversion rates & section effectiveness</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={FileText} label="Total Proposals" value={total} color="text-gray-600" bg="bg-gray-50" />
        <StatCard icon={DollarSign} label="Pipeline Value" value={formatCurrency(data.total_value)} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={CheckCircle2} label="Won Value" value={formatCurrency(data.won_value)} subtext={data.accepted_count > 0 ? `${data.accepted_count} deals` : undefined} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard icon={Target} label="Avg Deal Size" value={formatCurrency(data.avg_deal_size)} color="text-violet-600" bg="bg-violet-50" />
      </div>

      {/* Conversion Rates */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-gray-200">
          <CardContent className="p-5 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl font-bold text-blue-600">{data.send_rate}%</span>
            </div>
            <p className="text-sm font-medium text-gray-800">Send Rate</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Proposals that were sent to clients</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-5 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl font-bold text-emerald-600">{data.accept_rate}%</span>
            </div>
            <p className="text-sm font-medium text-gray-800">Win Rate</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Sent proposals that were accepted</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-5 text-center">
            <div className="w-16 h-16 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl font-bold text-violet-600">{data.avg_sections_per_proposal}</span>
            </div>
            <p className="text-sm font-medium text-gray-800">Avg Sections</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Sections filled per proposal</p>
          </CardContent>
        </Card>
      </div>

      {/* Two column layout: Funnel + Section Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Proposal Funnel */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="h-4 w-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-gray-800">Proposal Funnel</h3>
            </div>
            <div className="space-y-2">
              {funnelSteps.map(step => (
                <FunnelBar
                  key={step.label}
                  label={step.label}
                  count={step.count}
                  pct={total > 0 ? (step.count / total) * 100 : 0}
                  color={step.color}
                  icon={step.icon}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section Usage */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="h-4 w-4 text-blue-500" />
              <h3 className="text-sm font-semibold text-gray-800">Section Usage</h3>
              <span className="text-[10px] text-gray-400 ml-auto">Across all proposals</span>
            </div>
            <div className="space-y-2.5">
              {Object.entries(data.section_usage || {}).map(([key, count]) => (
                <ProgressBar
                  key={key}
                  label={SECTION_LABELS[key] || key}
                  value={count}
                  max={maxSectionUsage}
                  color={SECTION_COLORS[key] || '#6B7280'}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Timeline */}
      {data.timeline && data.timeline.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <h3 className="text-sm font-semibold text-gray-800">Monthly Activity</h3>
            </div>
            <div className="overflow-x-auto">
              <div className="flex gap-3 min-w-fit pb-2">
                {data.timeline.map(m => (
                  <div key={m.month} className="text-center min-w-[80px]">
                    <p className="text-[10px] text-gray-500 mb-2 font-medium">{m.month}</p>
                    <div className="flex gap-1 justify-center items-end h-16">
                      <div className="w-4 bg-gray-200 rounded-t" style={{ height: `${Math.max(4, m.created * 12)}px` }} title={`Created: ${m.created}`} />
                      <div className="w-4 bg-blue-400 rounded-t" style={{ height: `${Math.max(4, m.sent * 12)}px` }} title={`Sent: ${m.sent}`} />
                      <div className="w-4 bg-emerald-400 rounded-t" style={{ height: `${Math.max(4, m.accepted * 12)}px` }} title={`Accepted: ${m.accepted}`} />
                    </div>
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-[9px] text-gray-400">{m.created} created</p>
                      <p className="text-[9px] text-blue-500">{m.sent} sent</p>
                      <p className="text-[9px] text-emerald-500">{m.accepted} won</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 pt-2 border-t">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-gray-200 rounded" />
                <span className="text-[10px] text-gray-500">Created</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-blue-400 rounded" />
                <span className="text-[10px] text-gray-500">Sent</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-emerald-400 rounded" />
                <span className="text-[10px] text-gray-500">Won</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Activity */}
      <Card className="border-gray-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-800">Email Activity</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-2xl font-bold text-gray-900">{data.emails_sent}</p>
              <p className="text-xs text-gray-500">Total Emails Sent</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{data.proposal_emails_sent}</p>
              <p className="text-xs text-gray-500">Proposal Emails</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
